import { Component, EventEmitter, Output, inject } from '@angular/core';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '@nimmsta/ng-commons-lib/auth';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import {
  InputWithErrorComponent,
  SaveableFormComponent,
} from '@nimmsta/ng-commons-lib/forms';
import {
  SoftwareArtifact,
  SoftwareArtifactReleaseType,
  SoftwareArtifactStatus,
  UserRole,
} from '@nimmsta/ng-commons-lib/models';
import { SoftwareArtifactService } from '../services/software/software-artifact.service';
import { SoftwareArtifactReleaseTypeService } from '../services/software/software-artifact-release-type.service';

@Component({
  selector: 'app-software-artifact-form',
  templateUrl: 'software-artifact-form.component.html',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    InputWithErrorComponent,
    ErrorComponent,
  ],
})
export class SoftwareArtifactFormComponent extends SaveableFormComponent<SoftwareArtifact> {
  private fb = inject(FormBuilder);
  private softwareArtifactReleaseTypeService = inject(
    SoftwareArtifactReleaseTypeService,
  );

  protected override dataService = inject(SoftwareArtifactService);

  @Output() override closeEvent = new EventEmitter();

  @Output() override loadingEvent = new EventEmitter<boolean>();

  softwareArtifactStatusItems = Object.values(SoftwareArtifactStatus);

  softwareArtifactStatusNames?: string[];

  softwareArtifactReleaseTypes: SoftwareArtifactReleaseType[] = [];

  softwareArtifactReleaseTypeNames: string[] = [];

  softwareArtifactReleaseTypeIds: string[] = [];

  constructor() {
    const translate = inject(TranslateService);
    const authService = inject(AuthService);

    super();
    this.setValue({} as SoftwareArtifact);
    this.softwareArtifactStatusNames = this.softwareArtifactStatusItems.map(
      (item) =>
        translate.instant(`software.artifact.statusItems.${item.toLowerCase()}`),
    );

    // Only load software artifact release types if the user is an admin
    authService.getCurrentUser().subscribe((user) => {
      if (user.role === UserRole.ADMIN) {
        this.softwareArtifactReleaseTypeService.list().subscribe((page) => {
          this.softwareArtifactReleaseTypes = page.content;
          this.softwareArtifactReleaseTypeIds =
            this.softwareArtifactReleaseTypes.map((item) => item.id!);
          this.softwareArtifactReleaseTypeNames =
            this.softwareArtifactReleaseTypes.map((item) => item.name!);
        });
      }
    });
  }

  override onSave() {
    const selectedReleaseType = this.form.get('releaseType')?.value;
    if (selectedReleaseType) {
      this.value.releaseType =
        this.softwareArtifactReleaseTypes.find(
          (item) => item.id === selectedReleaseType,
        ) || undefined;
    }
    this.form.patchValue({
      ...this.value,
      ...this.form.value,
      releaseType: this.value.releaseType,
    });
    super.onSave();
  }

  protected setFormGroup(): void {
    this.form = this.fb.group({
      id: this.value.id,
      version: [this.value.version],
      buildNumber: [this.value.buildNumber],
      changelog: [this.value.changelog],
      size: [this.value.size],
      branchName: [this.value.branchName],
      status: [this.value.status, [Validators.required]],
      releaseType: [this.value.releaseType?.id],
      url: [this.value.url],
    });
  }

  protected override afterSetup() {
    this.form.controls['version'].disable();
    this.form.controls['buildNumber'].disable();
    this.form.controls['branchName'].disable();
    this.form.controls['size'].disable();
    this.form.controls['url'].disable();
  }
}
